// Mobile Contact Picker Service for OpsFlow

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Check if Contact Picker API is supported
export function isContactPickerSupported() {
  return 'contacts' in navigator && 'ContactsManager' in window;
}

// Get supported contact properties
export async function getSupportedProperties() {
  if (!isContactPickerSupported()) return [];
  
  try {
    return await navigator.contacts.getProperties();
  } catch {
    return ['name', 'email', 'tel']; // Default fallback
  }
}

// Open contact picker and select contacts
export async function pickContacts(options = {}) {
  if (!isContactPickerSupported()) {
    throw new Error('Contact Picker API not supported on this device');
  }

  const properties = options.properties || ['name', 'email', 'tel', 'address', 'icon'];
  const multiple = options.multiple !== false; // Default to multiple selection

  try {
    // Filter to only supported properties
    const supportedProps = await getSupportedProperties();
    const validProps = properties.filter(p => supportedProps.includes(p));
    
    const contacts = await navigator.contacts.select(
      validProps.length > 0 ? validProps : ['name', 'email', 'tel'],
      { multiple }
    );

    return contacts.map(contact => ({
      name: contact.name?.[0] || 'Unknown',
      email: contact.email?.[0] || null,
      phone: contact.tel?.[0] || null,
      company: null, // Contact Picker doesn't provide company
      position: null
    }));
  } catch (error) {
    if (error.name === 'InvalidStateError') {
      throw new Error('Please try again - picker was interrupted');
    }
    throw error;
  }
}

// Import selected contacts to OpsFlow
export async function importContactsToOpsFlow(contacts, token) {
  if (!contacts || contacts.length === 0) {
    throw new Error('No contacts to import');
  }

  const response = await fetch(`${API_URL}/api/pipeline/contacts/import-mobile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(contacts)
  });

  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.detail || 'Failed to import contacts');
  }

  return data;
}

// Combined function: Pick and import contacts
export async function pickAndImportContacts(token) {
  const contacts = await pickContacts();
  
  if (contacts.length === 0) {
    return { imported_count: 0, skipped_count: 0, message: 'No contacts selected' };
  }

  return importContactsToOpsFlow(contacts, token);
}
